import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Image as ImageIcon, Users, Eye } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

function AdminPhotoAlbums() {
  const navigate = useNavigate();
  const [albums, setAlbums] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEmployees();
  }, []);

  useEffect(() => {
    if (employees.length > 0) {
      fetchAlbums();
    }
  }, [selectedEmployeeId, employees]);

  const fetchEmployees = async () => {
    try {
      const token = localStorage.getItem('token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL;

      const response = await fetch(`${backendUrl}/api/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch employees');

      const users = await response.json();
      // Filter to only show employees and sub-contractors
      const employeeList = users.filter(
        u => u.role === 'employee' || u.role === 'sub_contractor'
      );
      setEmployees(employeeList);
    } catch (error) {
      console.error('Error fetching employees:', error);
      toast.error('Failed to load employees');
    } finally {
      setLoading(false);
    }
  };

  const fetchAlbums = async () => {
    try {
      const token = localStorage.getItem('token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL;

      let url = `${backendUrl}/api/photo-albums`;
      if (selectedEmployeeId !== 'all') {
        url += `?user_id=${selectedEmployeeId}`;
      }

      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch albums');

      const data = await response.json();
      setAlbums(data);
    } catch (error) {
      console.error('Error fetching albums:', error);
      toast.error('Failed to load albums');
    }
  };

  const getPrivacyBadgeColor = (privacy) => {
    switch (privacy) {
      case 'public':
        return 'bg-green-100 text-green-800';
      case 'team_visible':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPrivacyLabel = (privacy) => {
    switch (privacy) {
      case 'public':
        return 'Public';
      case 'team_visible':
        return 'Team';
      default:
        return 'Private';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">Employee Photo Albums</h1>
        <p className="text-gray-600 mb-6">View and manage all employee photo albums</p>

        {/* Employee Filter */}
        <div className="flex items-center gap-4">
          <label className="text-sm font-medium text-gray-700">Filter by Employee:</label>
          <Select value={selectedEmployeeId} onValueChange={setSelectedEmployeeId}>
            <SelectTrigger className="w-64">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Employees</SelectItem>
              {employees.map((emp) => (
                <SelectItem key={emp.id} value={emp.id}>
                  {emp.name} ({emp.role})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {albums.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <ImageIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">
              {selectedEmployeeId === 'all'
                ? 'No photo albums found'
                : 'This employee has no photo albums'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div>
          <p className="text-sm text-gray-600 mb-4">
            Showing {albums.length} album{albums.length !== 1 ? 's' : ''}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {albums.map((album) => (
              <Card key={album.id} className="hover:shadow-lg transition-shadow">
                <div className="h-48 bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                  <ImageIcon className="h-20 w-20 text-white opacity-50" />
                </div>
                <CardContent className="p-4">
                  <div className="mb-3">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">{album.name}</h3>
                    {album.description && (
                      <p className="text-sm text-gray-600 line-clamp-2 mb-2">{album.description}</p>
                    )}
                  </div>
                  <div className="flex items-center justify-between text-sm text-gray-600 mb-3">
                    <div className="flex items-center">
                      <Users className="h-4 w-4 mr-1" />
                      {album.user_name}
                    </div>
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${getPrivacyBadgeColor(
                        album.privacy_setting
                      )}`}
                    >
                      {getPrivacyLabel(album.privacy_setting)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">
                      {album.photo_count} photo{album.photo_count !== 1 ? 's' : ''}
                    </span>
                    <Button
                      size="sm"
                      onClick={() => navigate(`/photo-albums/${album.id}`)}
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      View
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminPhotoAlbums;
